import { useCallback, useRef } from "react";

import {
  clamp,
  createSmileSimulationProgressPlan,
  getSmileSimulationCompletionProgress,
  getSmileSimulationProgressAtElapsed,
} from "../lib/progress";
import {
  SMILE_SIMULATION_PROGRESS_BASE_MS,
  SMILE_SIMULATION_PROGRESS_FINISH_MAX_MS,
  SMILE_SIMULATION_PROGRESS_FINISH_MIN_MS,
  SMILE_SIMULATION_PROGRESS_INTERVAL_MS,
} from "../model/constants";
import type { SmileSimulationProgressPlan } from "../model/types";
import { useMountEffect } from "./useMountEffect";

type StopProgressOptions = {
  reset?: boolean;
};

type UseSmileSimulationProgressOptions = {
  onProgressChange: (progress: number) => void;
};

export function useSmileSimulationProgress({
  onProgressChange,
}: UseSmileSimulationProgressOptions) {
  const progressValueRef = useRef(0);
  const progressPlanRef = useRef<SmileSimulationProgressPlan | null>(null);
  const progressStartedAtRef = useRef<number | null>(null);
  const progressIntervalRef = useRef<number | null>(null);
  const progressFinishStartedAtRef = useRef<number | null>(null);
  const progressFinishFromRef = useRef(0);
  const progressFinishDurationRef = useRef(0);
  const progressFinishResolverRef = useRef<(() => void) | null>(null);

  const setProgressValue = useCallback(
    (nextProgress: number) => {
      const clampedProgress = clamp(nextProgress, 0, 100);

      progressValueRef.current = clampedProgress;
      onProgressChange(clampedProgress);
    },
    [onProgressChange],
  );

  const stopProgress = useCallback(
    (options?: StopProgressOptions) => {
      if (progressIntervalRef.current !== null) {
        window.clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }

      progressPlanRef.current = null;
      progressStartedAtRef.current = null;
      progressFinishStartedAtRef.current = null;
      progressFinishFromRef.current = 0;
      progressFinishDurationRef.current = 0;

      const resolver = progressFinishResolverRef.current;
      progressFinishResolverRef.current = null;
      resolver?.();

      if (options?.reset) {
        setProgressValue(0);
      }
    },
    [setProgressValue],
  );

  const startProgress = useCallback(
    (seed: number) => {
      stopProgress({ reset: true });
      progressPlanRef.current = createSmileSimulationProgressPlan(
        SMILE_SIMULATION_PROGRESS_BASE_MS,
        seed,
      );
      progressStartedAtRef.current = performance.now();

      progressIntervalRef.current = window.setInterval(() => {
        if (
          progressPlanRef.current == null ||
          progressStartedAtRef.current == null
        ) {
          return;
        }

        if (progressFinishStartedAtRef.current != null) {
          const completionElapsed =
            performance.now() - progressFinishStartedAtRef.current;
          const completionProgress = getSmileSimulationCompletionProgress(
            progressFinishFromRef.current,
            completionElapsed,
            progressFinishDurationRef.current,
          );

          setProgressValue(completionProgress);

          if (completionElapsed >= progressFinishDurationRef.current) {
            stopProgress();
            setProgressValue(100);
          }

          return;
        }

        const elapsedMs = performance.now() - progressStartedAtRef.current;
        const nextProgress = getSmileSimulationProgressAtElapsed(
          elapsedMs,
          progressPlanRef.current,
        );

        setProgressValue(nextProgress);
      }, SMILE_SIMULATION_PROGRESS_INTERVAL_MS);
    },
    [setProgressValue, stopProgress],
  );

  const finishProgress = useCallback(() => {
    if (progressValueRef.current >= 100) {
      setProgressValue(100);
      return Promise.resolve();
    }

    if (progressIntervalRef.current === null) {
      setProgressValue(100);
      return Promise.resolve();
    }

    progressFinishFromRef.current = progressValueRef.current;
    progressFinishDurationRef.current = clamp(
      220 + (100 - progressValueRef.current) * 6,
      SMILE_SIMULATION_PROGRESS_FINISH_MIN_MS,
      SMILE_SIMULATION_PROGRESS_FINISH_MAX_MS,
    );
    progressFinishStartedAtRef.current = performance.now();

    return new Promise<void>((resolve) => {
      progressFinishResolverRef.current = resolve;
    });
  }, [setProgressValue]);

  useMountEffect(() => {
    return () => {
      stopProgress();
    };
  });

  return {
    finishProgress,
    startProgress,
    stopProgress,
  };
}
